
import { Vue, Component, Watch, Ref } from "vue-property-decorator";
import Ace from "@/components/helpers/ace.vue";
import beautify from "js-beautify";
import editor from "./editor";

function insertHtmlAtCursor(html: string, target: HTMLElement) {
    if (!target || !target.isConnected) return;
    target.focus();

    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return;

    const range = selection.getRangeAt(0);
    range.deleteContents();

    const fragment = range.createContextualFragment(html);
    const lastChild = fragment.lastChild;

    range.insertNode(fragment);

    if (lastChild) {
        range.setStartAfter(lastChild);
    } else {
        range.setStartAfter(range.startContainer);
    }

    range.collapse(true);

    selection.removeAllRanges();
    selection.addRange(range);
}

@Component({
    mixins: [editor],
    components: { Ace }
})
export default class HmtlInlineEditor extends Vue {
    dialog: boolean = false;
    mode: number = 0;
    modeList: string[] = ["WYSIWYG", "HTML", "Просмотр"];
    save: any;
    cancelChanges: any;
    loading: boolean = false;

    content: string = "";
    htmlValue: string = "";
    value: string = "";

    showLinkModal: boolean = false;
    linkUrl: string = "";
    linkText: string = "";

    showImageModal: boolean = false;
    imageUrl: string = "";
    imageAlt: string = "";

    savedSelectionText: string = "";

    activeFormats = {
        bold: false,
        italic: false,
    };

    // === Цвет и размер шрифта ===
    currentColor: string = "#000000";
    colors = [
        // Базовые
        '#000000', // Black
        '#ffffff', // White
        '#ff0000', // Red
        '#00ff00', // Lime (Green)
        '#0000ff', // Blue

        // Основные цвета (CSS)
        '#ffff00', // Yellow
        '#00ffff', // Cyan
        '#ff00ff', // Magenta
        '#ffa500', // Orange
        '#800080', // Purple

        // Оттенки серого
        '#808080', // Gray
        '#c0c0c0', // Silver
        '#a9a9a9', // Dark Gray
        '#d3d3d3', // Light Gray
        '#696969', // Dim Gray

        // Пастельные и мягкие тона
        '#ff6347', // Tomato
        '#ffa07a', // Light Salmon
        '#98fb98', // Pale Green
        '#87ceeb', // Sky Blue
        '#dda0dd', // Plum

        // Яркие/насыщенные
        '#ff1493', // Deep Pink
        '#00ced1', // Dark Turquoise
        '#ff8c00', // Dark Orange
        '#32cd32', // Lime Green
        '#4b0082', // Indigo

        // Дополнительные популярные
        '#8a2be2', // Blue Violet
        '#ff69b4', // Hot Pink
        '#20b2aa', // Light Sea Green
        '#7fff00', // Chartreuse
        '#dc143c', // Crimson

        // Нейтральные и earth tones
        '#cd853f', // Peru
        '#deb887', // Burlywood
        '#556b2f', // Dark Olive Green
        '#8b4513', // Saddle Brown
        '#2f4f4f', // Dark Slate Gray
        '#483d8b'  // Dark Slate Blue
    ];
    fontSizes = [
        { text: "8", value: "1" },
        { text: "10", value: "2" },
        { text: "12", value: "3" },
        { text: "14", value: "4" },
        { text: "18", value: "5" },
        { text: "24", value: "6" },
        { text: "36", value: "7" },
    ];
    selectedFontSize: string = "3";

    // === ЕДИНСТВЕННОЕ место для хранения выделения ===
    private savedRange: Range | null = null;

    @Ref("editor") editorRef!: HTMLElement;

    @Watch("dialog")
    onDialogChange(val: boolean) {
        if (val) {
            this.content = this.value || "<p><br></p>";
            this.htmlValue = this.format(this.content);
            this.$nextTick(() => {
                if (this.mode === 0 && this.editorRef) {
                    this.editorRef.innerHTML = this.content;
                    this.updateActiveFormats();
                }
            });
        } else {
            this.value = this.format(this.content);
        }
    }

    onInput() {
        if (this.mode === 0) {
            this.content = this.editorRef.innerHTML;
        }
    }

    onHtmlInput(value: string) {
        this.htmlValue = value;
    }

    onTabChange() {
        if (this.mode === 1) {
            this.htmlValue = this.format(this.content);
        } else if (this.mode === 0) {
            this.content = this.htmlValue;
            this.$nextTick(() => {
                if (this.editorRef) {
                    this.editorRef.innerHTML = this.content;
                    this.updateActiveFormats();
                }
            });
        }
    }

    execCmd(command: string, value?: string) {
        document.execCommand(command, false, value);
        this.content = this.editorRef.innerHTML;
        this.updateActiveFormats();
    }

    updateActiveFormats() {
        this.$nextTick(() => {
            try {
                this.activeFormats.bold = document.queryCommandState("bold");
                this.activeFormats.italic = document.queryCommandState("italic");
            } catch (e) {
                console.warn("Не удалось определить состояние форматирования", e);
            }
        });
    }

    isFormatActive(format: string): boolean {
        return this.activeFormats[format] || false;
    }

    // === ЕДИНСТВЕННЫЙ метод сохранения выделения ===
    saveSelection() {
        const selection = window.getSelection();
        if (!selection || selection.rangeCount === 0 || !this.editorRef) {
            this.savedRange = null;
            return;
        }

        const range = selection.getRangeAt(0);
        if (!this.editorRef.contains(range.commonAncestorContainer)) {
            this.savedRange = null;
            return;
        }

        this.savedRange = range.cloneRange();
    }

    // === ЕДИНСТВЕННЫЙ метод восстановления выделения ===
    restoreSelection() {
        if (!this.savedRange || !this.editorRef) return;

        const selection = window.getSelection();
        if (selection) {
            selection.removeAllRanges();
            selection.addRange(this.savedRange);
            this.editorRef.focus();
        }
    }

    // === ЦВЕТ ===
    saveSelectionForColor() {
        this.saveSelection();
    }

    applyColor(color: string) {
        this.currentColor = color;
        this.restoreSelection();
        document.execCommand("foreColor", false, color);
        this.content = this.editorRef.innerHTML;
        // Опционально: обновить savedRange, если нужно продолжить редактирование
        // Но обычно после применения — выделение остаётся
    }

    // === РАЗМЕР ШРИФТА ===
    saveSelectionForFontSize() {
        this.saveSelection();
    }

    applyFontSize(size: string) {
        this.selectedFontSize = size;
        this.restoreSelection();
        document.execCommand("fontSize", false, size);
        this.content = this.editorRef.innerHTML;
    }

    // === ССЫЛКА ===
    promptLink() {
        this.saveSelection(); // Используем общий метод!
        const selection = window.getSelection();
        this.savedSelectionText = selection ? selection.toString() : "";
        this.linkUrl = "";
        this.linkText = this.savedSelectionText || "";
        this.showLinkModal = true;
    }

    insertLink() {
        if (!this.linkUrl || !this.dialog) return;

        const displayText = this.linkText || this.savedSelectionText || "ссылка";
        const html = `<a href="${this.linkUrl}" target="_blank">${displayText}</a>`;

        this.showLinkModal = false;

        this.$nextTick(() => {
            this.restoreSelection(); // Единый метод
            document.execCommand("insertHTML", false, html);
            this.content = this.editorRef.innerHTML;
            this.updateActiveFormats();
            this.savedRange = null; // Сбрасываем после вставки
        });
    }

    // === ИЗОБРАЖЕНИЕ ===
    promptImage() {
        this.saveSelection(); // Единый метод
        this.imageUrl = "";
        this.imageAlt = "";
        this.showImageModal = true;
    }

    insertImage() {
        if (!this.imageUrl || !this.dialog) return;

        const alt = this.imageAlt || "Изображение";
        const html = `<img src="${this.imageUrl}" alt="${alt}">`;

        this.showImageModal = false;

        this.$nextTick(() => {
            this.restoreSelection(); // Единый метод
            insertHtmlAtCursor(html, this.editorRef);
            this.content = this.editorRef.innerHTML;
            this.updateActiveFormats();
            this.savedRange = null;
        });
    }

    async saveChanges() {
        this.loading = true;
        try {
            if (this.mode === 1) {
                this.content = this.htmlValue;
            }
            this.value = this.format(this.content);
            await this.save();
        } catch (e) {
            console.error(e);
        } finally {
            this.loading = false;
            this.dialog = false;
        }
    }

    discartChanges() {
        this.cancelChanges();
        this.dialog = false;
    }

    // cleanWordGarbage(html) {
    //     if (!html) return "<p><br></p>";

    //     const isWord = /mso-|liberation|sans'|class=["'][^"']*doc[^"']*|xmlns:o=|<o:p>|<v:|<!\[if gte mso|style=["'][^"']*mso-/.test(html);

    //     if (!isWord) {
    //         return html;
    //     }

    //     const parser = new DOMParser();
    //     const doc = parser.parseFromString(html, "text/html");

    //     const allowedTags = new Set([
    //         "p", "h1", "h2", "h3", "h4", "h5", "h6",
    //         "ol", "ul", "li", "br", "a", "b", "strong", "i", "em"
    //     ]);

    //     const unwrapTags = ["span", "font", "o:p", "st1", "mark", "u", "strike", "div"];

    //     const cleanNode = (node) => {
    //         if (node.nodeType === Node.ELEMENT_NODE) {
    //             const tagName = node.tagName.toLowerCase();

    //             if (tagName === "a") {
    //                 const href = node.getAttribute("href");
    //                 while (node.attributes.length > 0) {
    //                     node.removeAttribute(node.attributes[0].name);
    //                 }
    //                 if (href) node.setAttribute("href", href);
    //             } else {
    //                 while (node.attributes.length > 0) {
    //                     node.removeAttribute(node.attributes[0].name);
    //                 }
    //             }

    //             if (unwrapTags.includes(tagName)) {
    //                 const parent = node.parentNode;
    //                 while (node.firstChild) {
    //                     parent.insertBefore(node.firstChild, node);
    //                 }
    //                 parent.removeChild(node);
    //                 return;
    //             }

    //             if (!allowedTags.has(tagName)) {
    //                 const p = doc.createElement("p");
    //                 while (node.firstChild) {
    //                     p.appendChild(node.firstChild);
    //                 }
    //                 node.parentNode.replaceChild(p, node);
    //                 node = p;
    //             }

    //             for (let i = 0; i < node.childNodes.length; i++) {
    //                 cleanNode(node.childNodes[i]);
    //             }
    //         }
    //     };

    //     Array.from(doc.body.childNodes).forEach(cleanNode);

    //     let cleaned = new XMLSerializer().serializeToString(doc.body)
    //         .replace(/^<body>|<\/body>$/gi, "")
    //         .replace(/&nbsp;/g, " ")
    //         .replace(/\s+/g, " ")
    //         .replace(/>\s+</g, "><");

    //     cleaned = cleaned
    //         .replace(/<span[^>]*>([\s\S]*?)<\/span>/gi, "$1")
    //         .replace(/<p>\s*<\/p>/g, "<p><br></p>")
    //         .replace(/<p><p([^>]*)>/g, "<p$1>")
    //         .replace(/<\/p><\/p>/g, "</p>")
    //         .replace(/<div><br><\/div>/g, "<p><br></p>");

    //     return cleaned.trim() || "<p><br></p>";
    // }

    // format(html: string) {
    //     if (!html) return "<p><br></p>";

    //     try {
    //         // 1. Чистка от Word-мусора (внутри cleanWordGarbage уже удаляются атрибуты, но не style)
    //         html = this.cleanWordGarbage(html);

    //         // 2. Заменяем <font> на <span style="..."> ДО удаления стилей
    //         html = html.replace(/<font[^>]*color\s*=\s*["']?([^"'>\s]+)["']?[^>]*>([\s\S]*?)<\/font>/gi, (match, color, content) => {
    //             return `<span style="color: ${color}">${content}</span>`;
    //         });

    //         html = html.replace(/<font[^>]*size\s*=\s*["']?([^"'>\s]+)["']?[^>]*>([\s\S]*?)<\/font>/gi, (match, size, content) => {
    //             const sizes: Record<string, string> = {
    //                 "1": "8px", "2": "10px", "3": "12px", "4": "14px",
    //                 "5": "18px", "6": "24px", "7": "36px"
    //             };
    //             const fontSize = sizes[size] || "14px";
    //             return `<span style="font-size: ${fontSize}">${content}</span>`;
    //         });

    //         // 3. Удаляем ЛИШЬ нежелательные стили, но сохраняем color и font-size
    //         // Ищем все теги со style и фильтруем содержимое атрибута
    //         html = html.replace(/<([^>]+?)\sstyle\s*=\s*["']([^"']*)["']([^>]*)>/gi, (match, before, styleAttr, after) => {
    //             // Разбираем style="..."
    //             const styles = styleAttr.split(';').map(s => s.trim()).filter(s => s);
    //             const keptStyles: string[] = [];

    //             for (const decl of styles) {
    //                 const [prop, val] = decl.split(':').map(p => p.trim().toLowerCase());
    //                 // Сохраняем ТОЛЬКО нужные свойства
    //                 if (prop === 'color' || prop === 'font-size') {
    //                     keptStyles.push(`${prop}: ${val}`);
    //                 }
    //             }

    //             if (keptStyles.length > 0) {
    //                 return `<${before} style="${keptStyles.join('; ')}"${after}>`;
    //             } else {
    //                 // Если не осталось стилей — удаляем атрибут style полностью
    //                 return `<${before}${after}>`;
    //             }
    //         });

    //         // 4. Остальная чистка (без изменения)
    //         html = html.replace(/&nbsp;/g, " ");
    //         html = html.replace(/<div(?![^>]*class=)([^>]*)>([\s\S]*?)<\/div>/g, (match, p1, p2) => {
    //             return `<p${p1}>${p2}</p>`;
    //         });
    //         html = html.replace(/<div><br><\/div>/g, "<p><br></p>");
    //         html = html
    //             .replace(/<p>\s*<ol>/g, "<ol>")
    //             .replace(/<p>\s*<ul>/g, "<ul>")
    //             .replace(/<\/ol>\s*<\/p>/g, "</ol>")
    //             .replace(/<\/ul>\s*<\/p>/g, "</ul>");
    //         html = html.replace(/<p><\/p>/g, "<p><br></p>");
    //         html = html
    //             .replace(/<p><p([^>]*)>/g, "<p$1>")
    //             .replace(/<\/p><\/p>/g, "</p>");
    //         html = html
    //             .replace(/^<body[^>]*>|<\/body>$/gi, "")
    //             .replace(/^<html[^>]*>|<\/html>$/gi, "")
    //             .replace(/^<!DOCTYPE[^>]*>/i, "");

    //         return beautify.html(html, {
    //             indent_size: 2,
    //             wrap_attributes: "force-aligned",
    //             end_with_newline: false,
    //             unformatted: ["code", "pre", "textarea"],
    //         });
    //     } catch (e) {
    //         console.error("Ошибка форматирования HTML", e);
    //         return html;
    //     }
    // }

    cleanWordGarbage(html) {
        if (!html) return "<p><br></p>";

        // ВАЖНО: не детектим Word по подстроке "doc" в class — она ложно срабатывает
        // на легитимные классы вёрстки (doctor-price, doctor-detail__* → содержат "doc"),
        // из-за чего чистилка сносила div/span и все атрибуты (class), ломая вставленный HTML.
        const isWord = /mso-|xmlns:o=|<o:p>|<v:|<!\[if gte mso|style=["'][^"']*mso-|class=["']Mso/.test(html);

        if (!isWord) {
            return html;
        }

        const parser = new DOMParser();
        const doc = parser.parseFromString(html, "text/html");

        const allowedTags = new Set([
            "p", "h1", "h2", "h3", "h4", "h5", "h6",
            "ol", "ul", "li", "br", "a", "b", "strong", "i", "em"
        ]);

        const unwrapTags = ["span", "font", "o:p", "st1", "mark", "u", "strike", "div"];

        const cleanNode = (node) => {
            if (node.nodeType === Node.ELEMENT_NODE) {
                const tagName = node.tagName.toLowerCase();

                if (tagName === "a") {
                    const href = node.getAttribute("href");
                    while (node.attributes.length > 0) {
                        node.removeAttribute(node.attributes[0].name);
                    }
                    if (href) node.setAttribute("href", href);
                } else {
                    while (node.attributes.length > 0) {
                        node.removeAttribute(node.attributes[0].name);
                    }
                }

                if (unwrapTags.includes(tagName)) {
                    const parent = node.parentNode;
                    while (node.firstChild) {
                        parent.insertBefore(node.firstChild, node);
                    }
                    parent.removeChild(node);
                    return;
                }

                if (!allowedTags.has(tagName)) {
                    const p = doc.createElement("p");
                    while (node.firstChild) {
                        p.appendChild(node.firstChild);
                    }
                    node.parentNode.replaceChild(p, node);
                    node = p;
                }

                for (let i = 0; i < node.childNodes.length; i++) {
                    cleanNode(node.childNodes[i]);
                }
            }
        };

        Array.from(doc.body.childNodes).forEach(cleanNode);

        let cleaned = new XMLSerializer().serializeToString(doc.body)
            .replace(/^<body>|<\/body>$/gi, "")
            .replace(/&nbsp;/g, " ")
            .replace(/\s+/g, " ")
            .replace(/>\s+</g, "><");

        cleaned = cleaned
            .replace(/<span[^>]*>([\s\S]*?)<\/span>/gi, "$1")
            .replace(/<p>\s*<\/p>/g, "<p><br></p>")
            .replace(/<p><p([^>]*)>/g, "<p$1>")
            .replace(/<\/p><\/p>/g, "</p>")
            .replace(/<div><br><\/div>/g, "<p><br></p>");

        return cleaned.trim() || "<p><br></p>";
    }

    format(html: string) {
        if (!html) return "<p><br></p>";

        try {
            // 1. Чистка от Word-мусора
            html = this.cleanWordGarbage(html);

            // 2. Заменяем <font> на <span style="..."> ДО удаления стилей
            html = html.replace(/<font[^>]*color\s*=\s*["']?([^"'>\s]+)["']?[^>]*>([\s\S]*?)<\/font>/gi, (match, color, content) => {
                return `<span style="color: ${color}">${content}</span>`;
            });

            html = html.replace(/<font[^>]*size\s*=\s*["']?([^"'>\s]+)["']?[^>]*>([\s\S]*?)<\/font>/gi, (match, size, content) => {
                const sizes: Record<string, string> = {
                    "1": "8px", "2": "10px", "3": "12px", "4": "14px",
                    "5": "18px", "6": "24px", "7": "36px"
                };
                const fontSize = sizes[size] || "14px";
                return `<span style="font-size: ${fontSize}">${content}</span>`;
            });

            // 3. Удаляем ЛИШЬ нежелательные стили, но сохраняем color и font-size
            html = html.replace(/<([^>]+?)\sstyle\s*=\s*["']([^"']*)["']([^>]*)>/gi, (match, before, styleAttr, after) => {
                const styles = styleAttr.split(';').map(s => s.trim()).filter(s => s);
                const keptStyles: string[] = [];

                for (const decl of styles) {
                    const [prop, val] = decl.split(':').map(p => p.trim().toLowerCase());
                    if (prop === 'color' || prop === 'font-size') {
                        keptStyles.push(`${prop}: ${val}`);
                    }
                }

                if (keptStyles.length > 0) {
                    return `<${before} style="${keptStyles.join('; ')}"${after}>`;
                } else {
                    return `<${before}${after}>`;
                }
            });

            // 4. Удаляем пустые спаны (без атрибутов) внутри параграфов
            html = html.replace(/<p[^>]*>(.*?)<\/p>/gi, (match, pContent) => {
                // Удаляем спаны без атрибутов внутри параграфа
                const cleanedContent = pContent.replace(/<span[^>]*>(.*?)<\/span>/gi, (spanMatch, spanContent, offset) => {
                    // Проверяем, есть ли у спана какие-либо атрибуты
                    const spanTag = spanMatch.substring(0, spanMatch.indexOf('>'));
                    const hasAttributes = spanTag.includes(' ') && (spanTag.includes('=') || spanTag.includes('style'));

                    if (!hasAttributes) {
                        return spanContent; // Удаляем span, оставляем только содержимое
                    }
                    return spanMatch; // Оставляем span с атрибутами
                });

                return `<p>${cleanedContent}</p>`;
            });

            // 5. Остальная чистка (без изменения)
            html = html.replace(/&nbsp;/g, " ");
            html = html.replace(/<div(?![^>]*class=)([^>]*)>([\s\S]*?)<\/div>/g, (match, p1, p2) => {
                return `<p${p1}>${p2}</p>`;
            });
            html = html.replace(/<div><br><\/div>/g, "<p><br></p>");
            html = html
                .replace(/<p>\s*<ol>/g, "<ol>")
                .replace(/<p>\s*<ul>/g, "<ul>")
                .replace(/<\/ol>\s*<\/p>/g, "</ol>")
                .replace(/<\/ul>\s*<\/p>/g, "</ul>");
            html = html.replace(/<p><\/p>/g, "<p><br></p>");
            html = html
                .replace(/<p><p([^>]*)>/g, "<p$1>")
                .replace(/<\/p><\/p>/g, "</p>");
            html = html
                .replace(/^<body[^>]*>|<\/body>$/gi, "")
                .replace(/^<html[^>]*>|<\/html>$/gi, "")
                .replace(/^<!DOCTYPE[^>]*>/i, "");

            return beautify.html(html, {
                indent_size: 2,
                wrap_attributes: "force-aligned",
                end_with_newline: false,
                unformatted: ["code", "pre", "textarea"],
            });
        } catch (e) {
            console.error("Ошибка форматирования HTML", e);
            return html;
        }
    }
}
